/**
 * Platform Branding — anonymous read hook + favicon installer
 * (2026-08-06, revised · Part D + Part F).
 *
 * Fetches the 3 active platform assets (login/favicon/website) from
 * `GET /api/public/platform-branding` and exposes URLs to any page.
 *
 * Also installs the favicon <link> tags on mount so the browser tab
 * icon reflects the Super Admin selection without a redeploy or
 * backend restart. Before appending versioned links, the installer
 * removes ALL conflicting favicon-related links so the document
 * always contains exactly one coherent set of platform favicon
 * declarations after React boots.
 *
 * Cache invalidation: modules that mutate branding (upload / replace /
 * remove) dispatch a ``platform-branding-updated`` window CustomEvent
 * which clears this module's in-tab cache and re-installs the favicon
 * for every open tab of the current session.
 */
import { useEffect, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

let _cached = null;   // in-tab cache — never blocks the paint
let _promise = null;

export function loadPlatformBranding(opts = {}) {
  const { force = false } = opts;
  if (force) {
    _cached = null;
    _promise = null;
  }
  if (_cached) return Promise.resolve(_cached);
  if (_promise) return _promise;
  _promise = fetch(`${BACKEND}/api/public/platform-branding`, { credentials: "omit" })
    .then(r => r.ok ? r.json() : { data: { assets: {} } })
    .then(d => {
      _cached = (d && d.data && d.data.assets) || {};
      return _cached;
    })
    .catch(() => { _cached = {}; return _cached; })
    .finally(() => { _promise = null; });
  return _promise;
}

/** Invalidate the in-tab cache. Any subsequent loadPlatformBranding()
 *  performs a fresh network fetch. */
export function invalidatePlatformBrandingCache() {
  _cached = null;
  _promise = null;
}

/** React hook — returns { login, favicon, website } URL strings, or
 *  null while loading. Consumers should render a fallback until ready. */
export function usePlatformBranding() {
  const [assets, setAssets] = useState(_cached);
  useEffect(() => {
    let alive = true;
    if (!_cached) {
      loadPlatformBranding().then(a => { if (alive) setAssets(a); });
    }
    // Listen for post-upload invalidations so every open tab picks up
    // the fresh asset without a page refresh.
    const onUpdated = () => {
      loadPlatformBranding({ force: true }).then(a => {
        if (alive) setAssets(a);
        installFaviconLinks(a?.favicon || null);
      });
    };
    window.addEventListener("platform-branding-updated", onUpdated);
    return () => {
      alive = false;
      window.removeEventListener("platform-branding-updated", onUpdated);
    };
  }, []);
  return assets;
}

/** Absolute URL helper. Returns `null` when the asset isn't configured
 *  so callers can fall back to the built-in /logo.svg. */
export function brandUrl(assetRecord, variant = "primary") {
  if (!assetRecord) return null;
  const url = assetRecord.urls?.[variant] || assetRecord.primary_url;
  if (!url) return null;
  return BACKEND + url;
}

/** Remove every favicon-adjacent <link> element from <head> so we
 *  can append a fresh coherent set. Includes bundled fallback tags
 *  (rel="icon" href="/favicon.ico"), branding-managed tags
 *  (data-branding='1'), and any tenant/legacy override tags. */
function _removeAllFaviconLinks() {
  const selectors = [
    "link[rel='icon']",
    "link[rel='shortcut icon']",
    "link[rel='alternate icon']",
    "link[rel='apple-touch-icon']",
    "link[rel='apple-touch-icon-precomposed']",
    "link[rel='mask-icon']",
  ];
  document.querySelectorAll(selectors.join(", ")).forEach(l => l.remove());
}

/** Install favicon <link> tags for every generated favicon variant.
 *  Safe to call multiple times — clears ALL prior favicon-related
 *  links first, then appends exactly one coherent set. If no active
 *  platform favicon exists, restores the stable root-alias links so
 *  browsers still see the packaged CertOpz fallback. */
export function installFaviconLinks(faviconAsset) {
  // 1) Always start from a clean slate.
  _removeAllFaviconLinks();

  const specs = [];

  if (faviconAsset) {
    // 2a) Platform favicon uploaded — inject versioned links.
    const urls = faviconAsset.urls || {};
    const stamp = faviconAsset.version || Date.now();
    if (urls["favicon.ico"]) {
      specs.push({ rel: "icon", type: "image/x-icon",
                    href: BACKEND + urls["favicon.ico"] + "?v=" + stamp,
                    sizes: "any" });
      specs.push({ rel: "shortcut icon",
                    href: BACKEND + urls["favicon.ico"] + "?v=" + stamp });
    }
    ["16x16.png", "32x32.png", "48x48.png", "192x192.png", "512x512.png"].forEach(v => {
      if (!urls[v]) return;
      specs.push({
        rel: "icon", type: "image/png", sizes: v.replace(".png", ""),
        href: BACKEND + urls[v] + "?v=" + stamp,
      });
    });
    if (urls["180x180.png"]) {
      specs.push({
        rel: "apple-touch-icon", sizes: "180x180",
        href: BACKEND + urls["180x180.png"] + "?v=" + stamp,
      });
    }
  } else {
    // 2b) No platform favicon — restore the bundled root-alias links so
    // the tab still shows the packaged CertOpz mark. These match the
    // pre-JS tags in ``index.html`` so crawlers and browsers see a
    // consistent set both before and after React boots.
    specs.push({ rel: "icon",          href: "/favicon.ico",         sizes: "any" });
    specs.push({ rel: "shortcut icon", href: "/favicon.ico" });
    specs.push({ rel: "icon", type: "image/png", sizes: "32x32",
                  href: "/favicon-32x32.png" });
    specs.push({ rel: "icon", type: "image/png", sizes: "192x192",
                  href: "/favicon-192x192.png" });
    specs.push({ rel: "apple-touch-icon", sizes: "180x180",
                  href: "/apple-touch-icon.png" });
  }

  for (const spec of specs) {
    const link = document.createElement("link");
    for (const [k, v] of Object.entries(spec)) link.setAttribute(k, v);
    link.setAttribute("data-branding", "1");
    document.head.appendChild(link);
  }
}

/** Fire this after a Super Admin upload / replace / remove so every
 *  open tab clears its in-tab cache and re-installs the favicon. */
export function notifyPlatformBrandingUpdated() {
  invalidatePlatformBrandingCache();
  try {
    window.dispatchEvent(new CustomEvent("platform-branding-updated"));
  } catch (_e) { /* older browsers — noop */ }
}
